import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Folder, FileText, Image } from '../../utils/icons';
import type { DriveItem } from '../../types/drive';
import { getFileTypeLabel, formatFileSize, formatModifiedDate, getMimeTypeIcon } from '../../utils/drive-helpers';

interface DriveItemCardProps {
  item: DriveItem;
}

const iconMap: Record<string, React.ComponentType<{ className?: string; size?: number }>> = {
  folder: Folder as any,
  pdf: FileText as any,
  image: Image as any,
  file: FileText as any,
};

const DriveItemCard: React.FC<DriveItemCardProps> = ({ item }) => {
  const navigate = useNavigate();
  const iconType = getMimeTypeIcon(item.mimeType);
  const IconComponent = iconMap[iconType];

  const handleClick = () => {
    if (item.isFolder) {
      navigate(`/dashboard/legal-forms/folder/${item.id}`);
    } else {
      navigate(`/dashboard/legal-forms/file/${item.id}`);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full text-left bg-white dark:bg-slate-800/50 rounded-2xl p-4 border border-gray-100 dark:border-slate-700/50 hover:border-primary/30 dark:hover:border-primary-dark/30 hover:shadow-md transition-all duration-200 cursor-pointer group"
    >
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
          item.isFolder
            ? 'bg-amber-50 dark:bg-amber-900/20'
            : 'bg-blue-50 dark:bg-blue-900/20'
        }`}>
          <IconComponent
            className={
              item.isFolder
                ? 'text-amber-500'
                : iconType === 'image'
                  ? 'text-emerald-500'
                  : 'text-blue-500'
            }
            size={20}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate group-hover:text-primary dark:group-hover:text-primary-dark transition-colors">
            {item.name}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-slate-400 dark:text-slate-500">
              {getFileTypeLabel(item.mimeType)}
            </span>
            {!item.isFolder && item.size && (
              <>
                <span className="text-xs text-slate-300 dark:text-slate-600">·</span>
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  {formatFileSize(item.size)}
                </span>
              </>
            )}
          </div>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
            {formatModifiedDate(item.modifiedTime)}
          </p>
        </div>
      </div>
    </button>
  );
};

export default DriveItemCard;
